import { CircularProgress, FormControl, InputLabel } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import {
  MultiInputDateRangeField,
  pickersLayoutClasses,
  PickersLayoutContentWrapper,
  PickersLayoutRoot,
  PickersTextField,
  usePickerLayout,
} from '@mui/x-date-pickers-pro';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import moment from 'moment';
import React, { memo, useCallback, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

import PickersLocalizationProvider from '../PickersLocalizationProvider/PickersLocalizationProvider';

const CustomPickersTextField = (props) => {
  const intl = useIntl();

  // Destructure to exclude internal MUI props that shouldn't reach DOM
  const {
    fieldDirection,
    isFieldDisabled,
    isFieldReadOnly,
    isFieldRequired,
    isPickerDisabled,
    isPickerReadOnly,
    isPickerOpen,
    isPickerValueEmpty,
    pickerOrientation,
    pickerVariant,
    ...restProps
  } = props;

  // Use position prop for reliable start/end detection (independent of locale/label text)
  const isStart = props.position === 'start';
  const label = isStart
    ? intl.formatMessage({ id: 'ControlledDateRangePicker.start' })
    : intl.formatMessage({ id: 'ControlledDateRangePicker.end' });

  return (
    <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
      <InputLabel htmlFor={props.id} shrink={false} sx={{ position: 'relative', transform: 'none' }}>
        {label}
      </InputLabel>
      {/* Override label to remove the internal floating label + notch */}
      <PickersTextField
        {...restProps}
        InputProps={{
          slotProps: {
            input: {
              sx: {
                width: '100%',
              },
            },
          },
        }}
        label={undefined}
        size="small"
      />
    </Stack>
  );
};

const CustomMultiInputRangeFieldSeparator = () => null;

const CustomMultiInputDateRangeField = ({ anchorRef, ...fieldProps }) => {
  return (
    <Box
      ref={anchorRef}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        width: '100%',
      }}
    >
      <MultiInputDateRangeField
        {...fieldProps}
        dateSeparator=""
        slots={{
          separator: CustomMultiInputRangeFieldSeparator,
          textField: CustomPickersTextField,
        }}
        sx={{
          flex: 1,
        }}
        useFlexGap={true}
      />
    </Box>
  );
};

CustomMultiInputDateRangeField.displayName = 'CustomMultiInputDateRangeField';

const CustomActionBar = ({ onClear, onCancel }) => {
  const intl = useIntl();
  return (
    <Stack
      direction="row"
      justifyContent="flex-end"
      spacing={1}
      sx={{ p: 2 }} // optional padding to match typical action bar look
    >
      <Button color="secondary" onClick={onClear} size="small" variant="text">
        {intl.formatMessage({ id: 'ControlledDateRangePicker.clear' })}
      </Button>
      <Button color="secondary" onClick={onCancel} size="small" variant="contained">
        {intl.formatMessage({ id: 'ControlledDateRangePicker.cancel' })}
      </Button>
    </Stack>
  );
};

const CustomLayout = (props) => {
  const { toolbar, tabs, content, actionBar, ownerState } = usePickerLayout(props);

  // Put the action bar before the content
  return (
    <PickersLayoutRoot className={pickersLayoutClasses.root} ownerState={ownerState}>
      {toolbar}
      <PickersLayoutContentWrapper
        className={pickersLayoutClasses.contentWrapper}
        ownerState={ownerState}
      >
        {tabs}
        {content}
        {actionBar}
      </PickersLayoutContentWrapper>
    </PickersLayoutRoot>
  );
};

const ControlledDateRangePickerComponent = ({
  name,
  control,
  error = false,
  helperText = '',
  controllerOptions = {},
  datePickerProps = {},
}) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [calendarKey, setCalendarKey] = useState(0);

  if (!name) {
    throw new Error('ControlledRangeTextField: name is required');
  }
  if (!(controllerOptions?.control || control)) {
    throw new Error('ControlledRangeTextField: control is required');
  }

  const handleClose = () => setOpen(false);

  // Handle open - trigger month change for existing value to fetch timeslots
  const handleOpen = useCallback((value) => {
    setOpen(true);
    // If there's an existing start date, trigger onMonthChange to fetch timeslots
    if (value?.[0] && datePickerProps.onMonthChange) {
      datePickerProps.onMonthChange(moment(value[0]));
    }
  }, [datePickerProps.onMonthChange]);

  // Handle clear - reset value and force calendar to remount at current month
  const handleClear = useCallback((onChange) => {
    onChange([null, null]);
    setCalendarKey((prev) => prev + 1);
    // Trigger onMonthChange to fetch timeslots for current month
    if (datePickerProps.onMonthChange) {
      datePickerProps.onMonthChange(moment());
    }
  }, [datePickerProps.onMonthChange]);

  return (
    <Controller
      control={control}
      name={name}
      {...controllerOptions}
      render={({ field, fieldState, formState }) => {
        // Validate that start and end dates are not the same (nightly bookings require at least 1 night)
        const handleDateChange = (newValue) => {
          const [start, end] = newValue;
          if (start && end && moment(start).isSame(moment(end), 'day')) {
            // Reset end date if same as start
            field.onChange([start, null]);
            return;
          }
          field.onChange(newValue);
        };

        return (
          <FormControl error={error}>
            <PickersLocalizationProvider>
              <DateRangePicker
                disablePast={true}
                key={calendarKey}
                onChange={handleDateChange}
                onClose={handleClose}
                onOpen={() => handleOpen(field.value)}
                open={open}
                referenceDate={moment()}
                renderLoading={() => <CircularProgress size={24} />}
                slotProps={{
                  actionBar: {
                    onClear: () => handleClear(field.onChange),
                    onCancel: handleClose,
                  },
                  field: { anchorRef },
                  popper: {
                    anchorEl: () => anchorRef.current,
                    disablePortal: true,
                    modifiers: [
                      {
                        name: 'flip',
                        enabled: false,
                        options: {
                          altBoundary: false,
                          rootBoundary: 'document',
                        },
                      },
                      {
                        name: 'preventOverflow',
                        enabled: true,
                        options: {
                          altAxis: true,
                          altBoundary: true,
                          tether: true,
                          rootBoundary: 'document',
                          padding: 0,
                        },
                      },
                    ],
                    placement: 'bottom-end',
                    sx: {
                      '& .MuiDayCalendar-loadingContainer': {
                        minWidth: '312px',
                        minHeight: '264px',
                      },
                    },
                  },
                  textField: (params) => ({
                    ...params,
                    onFocus: () => {
                      if (datePickerProps.onFieldFocused) {
                        datePickerProps.onFieldFocused(params.position);
                      }
                    },
                  }),
                }}
                slots={{
                  actionBar: CustomActionBar,
                  field: CustomMultiInputDateRangeField,
                  layout: CustomLayout,
                }}
                value={field.value}
                {...datePickerProps}
              />
            </PickersLocalizationProvider>
            {error ? <FormHelperText>{helperText}</FormHelperText> : null}
          </FormControl>
        );
      }}
    />
  );
};

ControlledDateRangePickerComponent.displayName = 'ControlledDatePicker';

const ControlledDatePicker = memo(ControlledDateRangePickerComponent);

export default ControlledDatePicker;